import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { jsonFetch } from '@/lib/api/jsonFetch'
import type {
  GeneratePdfResponse,
  IncidentListResponse,
  IncidentReport,
  IncidentStats,
  IncidentWrite,
} from './types'

const ROOT = '/api/warehouse-incidents'

export interface UseIncidentsOptions {
  status?: string | null
  search?: string
  page?: number
  limit?: number
}

export function useIncidents(opts: UseIncidentsOptions = {}) {
  const { status, search, page = 1, limit = 50 } = opts
  return useQuery({
    queryKey: ['warehouse-incidents', 'list', status, search, page, limit],
    queryFn: () => {
      const params = new URLSearchParams()
      if (status) params.set('status', status)
      if (search) params.set('search', search)
      params.set('page', String(page))
      params.set('limit', String(limit))
      return jsonFetch<IncidentListResponse>(`${ROOT}?${params}`)
    },
    staleTime: 15_000,
  })
}

export function useIncidentStats() {
  return useQuery({
    queryKey: ['warehouse-incidents', 'stats'],
    queryFn: () => jsonFetch<IncidentStats>(`${ROOT}/stats`),
    staleTime: 30_000,
  })
}

export function useIncident(id: string | null) {
  return useQuery({
    queryKey: ['warehouse-incidents', 'detail', id],
    queryFn: () => jsonFetch<IncidentReport>(`${ROOT}/${id}`),
    enabled: Boolean(id),
    staleTime: 15_000,
  })
}

export function useCreateIncident() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: Partial<IncidentWrite>) =>
      jsonFetch<IncidentReport>(ROOT, { method: 'POST', body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['warehouse-incidents'] })
    },
  })
}

export function useUpdateIncident(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      jsonFetch<IncidentReport>(`${ROOT}/${id}`, { method: 'PATCH', body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['warehouse-incidents'] })
    },
  })
}

export function useGeneratePdf(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      jsonFetch<GeneratePdfResponse>(`${ROOT}/${id}/generate-pdf`, { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['warehouse-incidents', 'detail', id] })
    },
  })
}

export function useLinkDiscrepancyReport(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (discrepancyReportId: string) =>
      jsonFetch<IncidentReport>(`${ROOT}/${id}/link`, {
        method: 'POST',
        body: { inventory_discrepancy_report_id: discrepancyReportId },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['warehouse-incidents'] })
      void qc.invalidateQueries({ queryKey: ['inventory-discrepancies'] })
    },
  })
}

export function useUnlinkDiscrepancyReport(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => jsonFetch<IncidentReport>(`${ROOT}/${id}/unlink`, { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['warehouse-incidents'] })
      void qc.invalidateQueries({ queryKey: ['inventory-discrepancies'] })
    },
  })
}

export function useDeleteIncident() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      jsonFetch<{ deleted: string }>(`${ROOT}/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['warehouse-incidents'] })
    },
  })
}
